import type { ClosureReason, OpenDetail, PoolStatus, ReopenEstimate, Report, SectionStatus } from "../shared/types";
import { isFavorite } from "./favorites";
import { CITY } from "./city";

/**
 * How likely a closed pool is to reopen before end of day:
 *  - "wait"            — likely transient, could reopen soon today (e.g. capacity turnover)
 *  - "come-back-later" — unlikely to reopen today / closed for the session (e.g. no lifeguard)
 *  - "uncertain"       — could go either way (e.g. weather)
 */
export type OutlookHint = "wait" | "come-back-later" | "uncertain";

/**
 * Maps each closure reason to its wait-vs-come-back outlook.
 * Pure function — no side effects, safe to unit-test.
 */
export function reasonOutlook(reason: ClosureReason): OutlookHint {
  switch (reason) {
    case "capacity":
      return "wait";
    case "no-lifeguard":
    case "maintenance":
    // A broken/low-pressure pad and a posted maintenance closure both read like
    // the pool "maintenance" case: not something that clears within the hour.
    case "broken":
    case "maintenance-posted":
      return "come-back-later";
    case "weather":
    case "other":
    // A pad that simply hasn't been turned on could come on at any point (or
    // not at all) — we have no basis to promise either way.
    case "not-turned-on":
      return "uncertain";
  }
}

/** Short, family-friendly label for an outlook hint. */
export function outlookLabel(hint: OutlookHint): string {
  switch (hint) {
    case "wait":
      return "Might reopen soon";
    case "come-back-later":
      return "Probably closed for the day";
    case "uncertain":
      return "Hard to say";
  }
}

/**
 * Builds the full tail appended to "Closed" in a timeline entry — e.g.
 * " · No lifeguard · Probably closed for the day · Reopening soon". The reason
 * contributes its label + outlook; an optional reopen estimate appends its label.
 * Both are optional so a closed report carrying only one still renders cleanly.
 * Exported as a pure helper so both list.ts and detail.ts stay in sync and the
 * string can be unit-tested independently of the DOM renderers.
 */
export function closedTimelineTail(
  reason?: ClosureReason,
  reopenEstimate?: ReopenEstimate,
): string {
  const parts: string[] = [];
  if (reason) parts.push(reasonLabel(reason), outlookLabel(reasonOutlook(reason)));
  const est = reopenEstimate ? reopenEstimateLabel(reopenEstimate) : "";
  if (est) parts.push(est);
  return parts.length ? ` · ${parts.join(" · ")}` : "";
}

/** Reopen estimates in display order; drives the reopen-estimate picker chips. */
export const REOPEN_ESTIMATES: ReopenEstimate[] = ["soon", "today", "tomorrow"];

/** Short label for a reopen estimate ("" for "unknown", which we don't surface). */
export function reopenEstimateLabel(estimate: ReopenEstimate): string {
  switch (estimate) {
    case "soon":
      return "Reopening soon";
    case "today":
      return "Later today";
    case "tomorrow":
      return "Tomorrow or later";
    case "unknown":
      return "";
  }
}

/** Chip label (shorter, imperative) for the reopen-estimate picker. */
function reopenChipLabel(estimate: ReopenEstimate): string {
  switch (estimate) {
    case "soon":
      return "Soon";
    case "today":
      return "Later today";
    case "tomorrow":
      return "Tomorrow+";
    case "unknown":
      return "";
  }
}

/**
 * Inline, optional reopen-estimate picker revealed after a closure reason is
 * chosen (the second row of the closed-report flow). "Skip" (empty data-reopen)
 * submits the closed report with no estimate — preserving the one-tap path.
 * Shared by the list and detail views so the chips stay identical. Hidden by
 * default; the view un-hides it after a reason chip is tapped.
 */
export function reopenPickerHtml(): string {
  const chips = REOPEN_ESTIMATES.map(
    (e) => `<button type="button" class="chip" data-reopen="${e}">${reopenChipLabel(e)}</button>`,
  ).join("");
  return `
    <div class="reopen-picker" data-role="reopen-picker" hidden>
      <span class="reason-prompt">Any idea when it'll reopen?</span>
      <div class="reason-chips">
        ${chips}
        <button type="button" class="chip chip-skip" data-reopen="">Skip</button>
      </div>
    </div>`;
}

/**
 * Copy for pools flagged `closedForSeason` (out all summer, e.g. renovation).
 * Shared by the list, detail, and map views so the wording stays identical.
 */
export const CLOSED_SEASON_BADGE = "Closed for 2026 season";
export const CLOSED_SEASON_NOTE = "Closed for renovation. Not open this summer.";

/**
 * Descriptor copy for pools flagged `slopedEntry`: the pool has a gradual
 * zero-depth entry and a shallow wading area suitable for small children — a
 * positive, factual feature (not emphasized as good or bad). Shared by the list,
 * detail, and map views so the wording stays identical.
 */
export const SLOPED_ENTRY_BADGE = "Sloped Entry/Wading Area";
export const SLOPED_ENTRY_NOTE =
  "Gradual zero-depth entry with a shallow wading area for small children.";

/**
 * Placeholder status badge shown before live status loads. Replaces the bare "…"
 * with a clear, accessible "Checking status…" affordance (a muted loading state)
 * that no longer reads as stray/clipped dots. The placeholder is kept (rather
 * than removed) so the card reserves space and there's no layout shift when the
 * real status lands; `applyStatus` later overwrites both text and class. Shared
 * by the list, detail, and splash views so the loading state stays identical.
 */
export const STATUS_LOADING_TEXT = "Checking status…";
export function statusBadgeHtml(): string {
  return `<span class="badge badge-loading" data-role="status">${STATUS_LOADING_TEXT}</span>`;
}

/**
 * Render a report timeline into the given <ol>, shared by the list, detail, and
 * splash views so the markup stays identical. Pure DOM writer — depends only on
 * the timeline helpers (relativeTime, closedTimelineTail) that live here too.
 */
export function renderTimeline(listEl: HTMLElement, reports: Report[]): void {
  if (reports.length === 0) {
    listEl.innerHTML = '<li class="muted">No reports in the last 24h.</li>';
    return;
  }
  const now = Date.now();
  listEl.innerHTML = reports
    .map((r) => {
      const dot = r.status === "open" ? "dot-open" : "dot-closed";
      const word = r.status === "open" ? "Open" : "Closed";
      // Closed reports may carry an optional reason + outlook + reopen estimate,
      // e.g. "Closed · No lifeguard · Probably closed for the day · Reopening soon".
      const tail = r.status === "closed" ? closedTimelineTail(r.reason, r.reopenEstimate) : "";
      return `<li class="timeline-item">
        <span class="timeline-dot ${dot}"></span>
        <span class="timeline-status">${word}${tail}</span>
        <span class="timeline-time">${relativeTime(r.ts, now)}</span>
      </li>`;
    })
    .join("");
}

/**
 * Strip the redundant trailing ", <City>, <ST> <zip>" suffix from a stored
 * address for display — noise on every card since a site only covers one city.
 * The suffix pattern is city-specific (`CITY.addressSuffixPattern`): DC strips
 * ", Washington, DC <zip>", Arlington strips ", Arlington, VA <zip>". Tolerant of
 * the no-zip and ZIP+4 variants and case-insensitive. The FULL address is still
 * used for the maps link query (geocoding is more reliable with city/zip) — see
 * `mapsHref`.
 */
export function displayAddress(address: string): string {
  return address.replace(CITY.addressSuffixPattern, "").trim();
}

/**
 * Platform-aware maps deep link for a full address. iOS (iPhone/iPad) opens
 * Apple Maps; Android and desktop open Google Maps (the app if installed, else
 * the browser). Always queries the FULL stored address (with city/zip) so the
 * geocode is unambiguous.
 *
 * iPadOS 13+ changed its UA to look like a desktop Mac, so we cannot rely on
 * the UA alone. The fallback check (`Macintosh` + `maxTouchPoints > 1`) catches
 * iPadOS 13+ while leaving real Macs unaffected (Macs report 0 or undefined).
 */
export function mapsHref(fullAddress: string): string {
  const q = encodeURIComponent(fullAddress);
  if (typeof navigator === "undefined") return `https://www.google.com/maps/search/?api=1&query=${q}`;
  const ua = navigator.userAgent;
  const maxTouch = navigator.maxTouchPoints ?? 0;
  const isIOS = /iPad|iPhone|iPod/.test(ua) || (/Macintosh/.test(ua) && maxTouch > 1);
  if (isIOS) return `https://maps.apple.com/?q=${q}`;
  return `https://www.google.com/maps/search/?api=1&query=${q}`;
}

/**
 * Address anchor shared by every view: shows the SHORT `displayAddress()` (with
 * the ", Washington, DC <zip>" tail stripped) as escaped link text, and links to
 * the right maps app for the platform using the FULL stored address. Opens in a
 * new tab. Defined once so the markup stays identical across list/detail/map/splash.
 */
export function addressHtml(fullAddress: string): string {
  return `<a class="addr-link" href="${mapsHref(fullAddress)}" target="_blank" rel="noopener">${esc(displayAddress(fullAddress))}</a>`;
}

/**
 * "Use my location" control labels. The button now lives inline in the header
 * pill row (beside Contact / Donate), so the visible labels are deliberately
 * SHORT to fit a phone row; the full intent rides in the button's aria-label /
 * title. Shared by the list and splash views so the wording + states stay
 * identical:
 *  - IDLE     — not yet located (and after a failed/denied attempt)
 *  - LOCATING — transient, while the fix is in flight
 *  - LOCATED  — a fix is in hand and the list is sorted nearest-first (disabled)
 */
export const GEO_LABEL_IDLE = "📍 Nearby";
export const GEO_LABEL_LOCATING = "📍 Locating…";
export const GEO_LABEL_LOCATED = "📍 Nearest ✓";
/** Full description for the locate button's aria-label / title (the short visible label needs context). */
export const GEO_LABEL_FULL = "Use my location to sort pools nearest first";

/**
 * Banner copy shown when the browser reports the location permission as
 * denied. Deliberately platform-agnostic: the previous "click the lock/
 * site-info icon in the address bar" wording described a desktop-Chrome-only
 * affordance and meant nothing on iOS Safari or Android Chrome, where there is
 * no lock icon in that spot. Centralized here (rather than duplicated per
 * view) so list, splash, and map can't drift apart again — see 8d30e48b.
 */
export const GEO_DENIED_COPY =
  "Location is blocked. Allow location access for this site in your browser settings, then tap again.";

/**
 * The "Use my location" pill, rendered inline in the header alongside the
 * Contact / Donate links. Carries the `data-action="locate"` hook the views wire
 * and a descriptive aria-label/title (the visible label is the short
 * `GEO_LABEL_IDLE`). Shared by the list and splash views so the markup stays
 * identical.
 */
export function geoButtonHtml(): string {
  return `<button class="geo-btn" data-action="locate" type="button" aria-label="${GEO_LABEL_FULL}" title="${GEO_LABEL_FULL}">${GEO_LABEL_IDLE}</button>`;
}

/**
 * Star toggle button for a pool or splash-pad card: reflects the current
 * persisted favorite state and exposes a `data-action="favorite"` hook for
 * event delegation in the view layer. Shared by the list and splash views so
 * the markup and aria attributes stay identical.
 *
 * The glyph swaps filled (★) vs outline (☆) so favorite state is never
 * conveyed by color alone, and the aria-label spells out what the toggle does
 * ("pins to top") rather than just naming the action — screen-reader users get
 * the same context sighted users get from the card reordering on favorite
 * (0fb64a9d). The `.fav-toggle` class itself (styled in style.css) is a real
 * control now: a bordered, ≥44px tap target instead of a bare glyph.
 */
export function favToggleHtml(poolId: string): string {
  const fav = isFavorite(poolId);
  const label = fav ? "Remove favorite — currently pinned to top" : "Favorite — pins to top";
  return `<button class="fav-toggle${fav ? " is-fav" : ""}" data-action="favorite" aria-pressed="${fav}" aria-label="${label}" title="${label}">${fav ? "★" : "☆"}</button>`;
}

/**
 * Quiet descriptor line for a pool with a sloped (zero-depth) entry and wading
 * area. Demoted from a blue pill to a muted single line with a small icon (a
 * secondary fact, not a status — it should not carry the same visual weight as
 * "is it open"). The full description rides in the `title`. Reused by the list
 * and detail views.
 */
export function slopedEntryBadgeHtml(): string {
  return `<span class="sloped-tag" title="${SLOPED_ENTRY_NOTE}"><span class="sloped-icon" aria-hidden="true">◡</span> ${SLOPED_ENTRY_BADGE}</span>`;
}

/** Closure reasons in display order; drives the inline reason picker chips. */
export const CLOSURE_REASONS: ClosureReason[] = [
  "no-lifeguard",
  "weather",
  "maintenance",
  "capacity",
  "other",
];

/**
 * The closure reasons that make sense for a SPLASH PAD. A pad is unstaffed, so
 * the pool reasons (no lifeguard, at capacity, weather) never apply and the
 * generic "Maintenance"/"Other" pair asked the reporter to guess. These three
 * are what a person standing at the pad can actually observe: the water isn't
 * on at all, it's on but broken/dribbling, or there's a maintenance notice
 * posted. Disjoint from `CLOSURE_REASONS` by design (see ClosureReason).
 */
export const SPLASH_CLOSURE_REASONS: ClosureReason[] = [
  "not-turned-on",
  "broken",
  "maintenance-posted",
];

/** Human-readable label for a closure reason (also used in the timeline). */
export function reasonLabel(reason: ClosureReason): string {
  switch (reason) {
    case "no-lifeguard":
      return "No lifeguard";
    case "weather":
      return "Weather";
    case "maintenance":
      return "Maintenance";
    case "capacity":
      return "At capacity";
    case "other":
      return "Other";
    case "not-turned-on":
      return "Not turned on";
    case "broken":
      return "Broken or low pressure";
    case "maintenance-posted":
      return "Maintenance posted";
  }
}

/**
 * Inline, optional reason picker revealed after tapping "Mark as Closed".
 * Shared by the list and detail views so the chips stay identical. "Skip"
 * (empty data-reason) submits a closed report with no reason — preserving the
 * one-tap path. Hidden by default; the view un-hides it on the closed tap.
 */
export function reasonPickerHtml(reasons: ClosureReason[] = CLOSURE_REASONS): string {
  const chips = reasons.map(
    (r) => `<button type="button" class="chip" data-reason="${r}">${reasonLabel(r)}</button>`,
  ).join("");
  return `
    <div class="reason-picker" data-role="reason-picker" hidden>
      <span class="reason-prompt">Why is it closed? Helps others know whether to wait.</span>
      <div class="reason-chips">
        ${chips}
        <button type="button" class="chip chip-skip" data-reason="">Skip</button>
      </div>
    </div>`;
}

/** Open-details in display order; drives the optional open-detail picker chips. */
export const OPEN_DETAILS: OpenDetail[] = ["long-line", "crowded", "uncrowded", "one-in-one-out"];

/**
 * The open-details that make sense for a SPLASH PAD. A pad has no entry line
 * and no one-in-one-out capacity gate (both are lifeguard-managed pool
 * mechanics), so only the crowding pair is offered there.
 */
export const SPLASH_OPEN_DETAILS: OpenDetail[] = ["crowded", "uncrowded"];

/** Human-readable label for an open-detail (also appended to the freshness line). */
export function openDetailLabel(detail: OpenDetail): string {
  switch (detail) {
    case "long-line":
      return "Long line";
    case "crowded":
      return "Crowded";
    case "uncrowded":
      return "Not crowded";
    case "one-in-one-out":
      return "One in/one out";
  }
}

/**
 * Inline, optional open-detail picker revealed AFTER a one-tap "Mark as Open"
 * submits — an enrichment, not a gate. The plain open report has already landed,
 * so tapping a chip sends a SECOND open report carrying that detail; "Skip"
 * (empty data-detail) just collapses the picker and sends nothing. Mirrors
 * `reasonPickerHtml`'s markup so the chip styling is shared. Hidden by default;
 * the view un-hides it once the open report has been fired.
 *
 * Parameterized like `reasonPickerHtml`: splash pads pass
 * `SPLASH_OPEN_DETAILS` (crowding only — a pad has no line or capacity gate).
 */
export function openDetailPickerHtml(details: OpenDetail[] = OPEN_DETAILS): string {
  const chips = details.map(
    (d) => `<button type="button" class="chip" data-detail="${d}">${openDetailLabel(d)}</button>`,
  ).join("");
  return `
    <div class="open-detail-picker" data-role="open-detail-picker" hidden>
      <span class="reason-prompt">Add detail? Helps others know what to expect.</span>
      <div class="reason-chips">
        ${chips}
        <button type="button" class="chip chip-skip" data-detail="">Skip</button>
      </div>
    </div>`;
}

// Pool fields come from the API/DB rather than a static constant, so escape
// them before interpolating into innerHTML.
export function esc(s: string): string {
  return s.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
}

/**
 * Shared site footer used by the list, splash, and detail views.
 *
 * Includes the deep link to the city's official status page with the mandatory
 * caveat about what that page does and doesn't cover, plus an About link. All
 * copy is city-specific (`CITY.footNote` / `statusUrl` / `statusLinkLabel` /
 * `statusCaveat`). Defined once so the wording stays identical across views.
 *
 * `variant` swaps ONLY the explainer sentence: the default pool copy is about
 * children's-section status, which is meaningless on a splash pad's page, so
 * splash surfaces (the Splash tab and splash detail pages) pass `"splash"` to
 * get `CITY.splashStatusCaveat` instead. Everything else is shared.
 */
export type FooterVariant = "pool" | "splash";

export function footerHtml(variant: FooterVariant = "pool"): string {
  const caveat = variant === "splash" ? CITY.splashStatusCaveat : CITY.statusCaveat;
  return `
    <footer class="foot">
      <p class="foot-note">${CITY.footNote}</p>
      <p class="foot-splash">
        <a href="${CITY.statusUrl}" target="_blank" rel="noopener noreferrer">${CITY.statusLinkLabel}</a>.
        ${caveat}
      </p>
      <p class="foot-links"><a href="#/about">About</a></p>
    </footer>`;
}

export interface StatusBadge {
  text: string;
  cls: string;
}

/** Human-readable badge for a section's live status. */
export function statusLabel(s: SectionStatus): StatusBadge {
  const count = s.recentReportCount;
  const tail = count > 0 ? ` · ${count} report${count === 1 ? "" : "s"}` : "";
  if (s.reported === "open") return { text: `Open now${tail}`, cls: "badge-open" };
  if (s.reported === "closed") {
    return {
      text: s.conflictsWithSchedule ? `Closed, should be open${tail}` : `Closed now${tail}`,
      cls: "badge-closed",
    };
  }
  // No recent reports — fall back to the DPR schedule.
  switch (s.scheduled) {
    case "open":
      return { text: "Scheduled open · no reports", cls: "badge-open" };
    case "closed":
      return { text: "Closed (schedule)", cls: "badge-closed" };
    case "out-of-season":
      return { text: "Out of season", cls: "badge-unknown" };
    default:
      return { text: "No info yet", cls: "badge-unknown" };
  }
}

/**
 * Badge for a pool's water-slide section. The slide defaults OFF, so absent any
 * crowd report it reads "Off" with a nudge to report if it's running; a crowd
 * "open" (running) report flips it to "On". A "closed" report reads a plain
 * "Off" (no nudge). There is no Unknown resting state.
 *
 * A closed-for-season pool forces the slide out-of-season (see computeSlideStatus):
 * it reads a fixed "Off · closed for season" with no nudge, since the section is
 * informational (non-reportable) there.
 */
export function slideBadge(s: SectionStatus | undefined): StatusBadge {
  if (s?.scheduled === "out-of-season")
    return { text: "🛝 Slide: Off · closed for season", cls: "badge-slide-off" };
  if (s?.reported === "open") return { text: "🛝 Slide: On", cls: "badge-open" };
  const hint = s?.reported === "closed" ? "" : " · usually off, tap if it's running";
  return { text: `🛝 Slide: Off${hint}`, cls: "badge-slide-off" };
}

/**
 * Direction-1 "Confidence-led" section view. Splits a section's live status into
 * the four pieces the card/detail tile renders:
 *  - `word`    bold status word (always paired with color → never color-only, WCAG 1.4.1)
 *  - `qual`    muted source qualifier, e.g. "· per DPR schedule" ("" when none)
 *  - `hero`    freshness "hero" line, carrying a glyph (✓ confirmed, △ conflict)
 *  - `accent`  drives the tile's left-accent + status-word color
 *  - `heroTone`hero text color tone ("" = muted default)
 *  - `openBtn` / `closedBtn`  confidence-voice report-button labels
 *
 * Core principle: confirmed ≠ scheduled. Crowd-confirmed states are solid
 * green/red (with a dedicated `conflict` red when DPR says open but the crowd
 * says closed); schedule-only "expected" states are amber, so a parent can tell a
 * just-confirmed pool from one that's merely "open per the schedule, unconfirmed".
 */
export type StatusAccent = "open" | "closed" | "sched" | "neutral" | "conflict";
export type HeroTone = "good" | "bad" | "warn" | "";

export interface SectionView {
  word: string;
  qual: string;
  hero: string;
  accent: StatusAccent;
  heroTone: HeroTone;
  openBtn: string;
  closedBtn: string;
}

/** All section accents — used to clear stale accent classes before painting. */
export const STATUS_ACCENTS: StatusAccent[] = ["open", "closed", "sched", "neutral", "conflict"];

/** "1 person" / "N people" for a recent-report count (always ≥1 when reported). */
function peopleCount(count: number): string {
  return count === 1 ? "1 person" : `${count} people`;
}

/**
 * Map a section's live status to its Direction-1 view. Reports win over the
 * schedule (confirmed); absent reports we fall back to the DPR schedule
 * (expected-only, amber). The slide has its own resting-Off model — see
 * `slideView`.
 */
export function sectionStatusView(s: SectionStatus, now = Date.now()): SectionView {
  const ago = s.lastReportedTs != null ? ` · ${relativeTime(s.lastReportedTs, now)}` : "";

  // Crowd-confirmed open: solid green, freshness is the hero. Counts ONLY genuine
  // open votes (recentOpenCount) — not closed reports that lost the decay vote.
  if (s.reported === "open") {
    const people = peopleCount(s.recentOpenCount);
    const detail = s.openDetail ? ` · ${openDetailLabel(s.openDetail)}` : "";
    return {
      word: "Open now",
      qual: "",
      hero: `✓ Confirmed by ${people}${ago}${detail}`,
      accent: "open",
      heroTone: "good",
      openBtn: "Still open",
      closedBtn: "It's closed",
    };
  }
  // Crowd-confirmed closed: conflict (DPR open vs crowd closed) gets its own
  // dedicated red treatment; an otherwise-closed report reads a plain "Closed now".
  if (s.reported === "closed") {
    // When the kiddie was force-closed by the main (closedByMain set), cite the
    // MAIN pool's closed vote count with "main pool" wording — the kiddie's own
    // recentClosedCount is 0 so "0 people reported closed" would be misleading.
    if (s.closedByMain != null) {
      const people = peopleCount(s.closedByMain);
      if (s.conflictsWithSchedule) {
        return {
          word: "Reported closed",
          qual: "· though DPR says open",
          hero: `△ ${people} reported the main pool closed${ago}`,
          accent: "conflict",
          heroTone: "bad",
          openBtn: "It's open",
          closedBtn: "Still closed",
        };
      }
      return {
        word: "Closed now",
        qual: "",
        hero: `${people} reported the main pool closed${ago}`,
        accent: "closed",
        heroTone: "bad",
        openBtn: "It's open",
        closedBtn: "It's closed",
      };
    }
    // Count ONLY genuine closed votes (recentClosedCount) — never crowd "open"
    // reports that were suppressed to closed by the schedule cross-check.
    const people = peopleCount(s.recentClosedCount);
    if (s.conflictsWithSchedule) {
      return {
        word: "Reported closed",
        qual: "· though DPR says open",
        hero: `△ ${people} reported closed${ago}`,
        accent: "conflict",
        heroTone: "bad",
        openBtn: "It's open",
        closedBtn: "Still closed",
      };
    }
    return {
      word: "Closed now",
      qual: "",
      hero: `${people} reported closed${ago}`,
      accent: "closed",
      heroTone: "bad",
      openBtn: "It's open",
      closedBtn: "It's closed",
    };
  }

  // No recent crowd reports — fall back to the DPR schedule (expected, unconfirmed).
  switch (s.scheduled) {
    case "open":
      return {
        word: "Likely open",
        qual: "· per DPR schedule",
        hero: "No reports yet today",
        accent: "sched",
        heroTone: "warn",
        openBtn: "It's open",
        closedBtn: "It's closed",
      };
    case "closed":
      return {
        word: "Likely closed",
        qual: "· per DPR schedule",
        hero: "No one's confirmed today — tap if you're there",
        accent: "sched",
        heroTone: "warn",
        openBtn: "It's open",
        closedBtn: "It's closed",
      };
    case "out-of-season":
      return {
        word: "Out of season",
        qual: "",
        hero: "",
        accent: "neutral",
        heroTone: "",
        openBtn: "It's open",
        closedBtn: "It's closed",
      };
    default:
      return {
        word: "No info yet",
        qual: "",
        hero: "",
        accent: "neutral",
        heroTone: "",
        openBtn: "It's open",
        closedBtn: "It's closed",
      };
  }
}

/**
 * Direction-1 view for a pool's water slide. The slide defaults OFF (resting,
 * neutral): absent a crowd report it reads "Off · usually off" with a "Tap if
 * it's running" nudge; a crowd "open" (running) report flips it to a confirmed
 * green "Running". A closed-for-season pool's slide reads a fixed "Off · closed
 * for season". Buttons read Running / Off (no redundant "Slide:" prefix — the
 * section already carries the "🛝 Water slide" label).
 */
export function slideView(s: SectionStatus | undefined, now = Date.now()): SectionView {
  const base: Pick<SectionView, "openBtn" | "closedBtn"> = { openBtn: "Running", closedBtn: "Off" };
  if (s?.scheduled === "out-of-season")
    return { word: "Off", qual: "· closed for season", hero: "", accent: "neutral", heroTone: "", ...base };
  if (s?.reported === "open") {
    const ago = s.lastReportedTs != null ? ` · ${relativeTime(s.lastReportedTs, now)}` : "";
    return { word: "Running", qual: "", hero: `✓ Reported running${ago}`, accent: "open", heroTone: "good", ...base };
  }
  return { word: "Off", qual: "· usually off", hero: "Tap if it's running", accent: "neutral", heroTone: "", ...base };
}

/**
 * Paint a section tile (list card or detail page) from a SectionView: the tile's
 * left-accent color, the bold status word + muted qualifier, the freshness hero
 * line, and the confidence-voice button labels. Shared by the list and detail
 * views so the Direction-1 treatment stays identical. Keeps the `data-role` hooks
 * (`status` / `qual` / `freshness`) stable so the report wiring and status fetch
 * keep working; disabled (closed-for-season) buttons carry no `data-action` and
 * are left untouched.
 */
export function paintSectionStatus(tile: HTMLElement, view: SectionView): void {
  for (const a of STATUS_ACCENTS) tile.classList.toggle(`sec-acc-${a}`, a === view.accent);
  const word = tile.querySelector<HTMLElement>('[data-role="status"]');
  if (word) {
    word.textContent = view.word;
    word.className = `status status-${view.accent}`;
  }
  const qual = tile.querySelector<HTMLElement>('[data-role="qual"]');
  if (qual) qual.textContent = view.qual;
  const hero = tile.querySelector<HTMLElement>('[data-role="freshness"]');
  if (hero) {
    hero.textContent = view.hero;
    hero.className = `hero${view.heroTone ? ` ${view.heroTone}` : ""}`;
  }
  const openBtn = tile.querySelector<HTMLElement>('[data-action="report"][data-status="open"]');
  if (openBtn) openBtn.textContent = view.openBtn;
  const closedBtn = tile.querySelector<HTMLElement>('[data-action="report"][data-status="closed"]');
  if (closedBtn) closedBtn.textContent = view.closedBtn;
}

/**
 * Initial status-line markup for a section tile (list + detail). Renders the bold
 * status word (a loading placeholder until live status lands) plus an empty
 * qualifier slot; `paintSectionStatus` fills both once status is known. The
 * `data-role="status"` hook is preserved so the optimistic report path and the
 * status fetch keep targeting it.
 */
export function statusLineHtml(): string {
  return `<p class="status-line"><span class="status status-neutral" data-role="status">${STATUS_LOADING_TEXT}</span> <span class="qual" data-role="qual"></span></p>`;
}

/**
 * Reliability label for a pool: "Reported closed N of last M open days", or
 * null when there's nothing to show. We return null both when reliability is
 * absent (not computed — e.g. offline/sample data) and when it's null
 * (computed but below the data threshold), so callers hide the row entirely
 * rather than surfacing a vague "not enough data yet" message to end users.
 */
export function reliabilityLabel(reliability: PoolStatus["reliability"]): string | null {
  if (reliability == null) return null;
  const { conflictDays, scheduledOpenDays } = reliability;
  return `Reported closed ${conflictDays} of last ${scheduledOpenDays} open days`;
}

/** "open" | "closed" | "unknown" — drives map marker color and open-now filter. */
export type EffectiveStatus = "open" | "closed" | "unknown";

/** What we believe is true right now: reports win, else schedule. */
export function effectiveStatus(s: SectionStatus): EffectiveStatus {
  if (s.reported === "open") return "open";
  if (s.reported === "closed") return "closed";
  if (s.scheduled === "open") return "open";
  if (s.scheduled === "closed" || s.scheduled === "out-of-season") return "closed";
  return "unknown";
}

/** Relative time like "just now", "5 min ago", "3 h ago". */
export function relativeTime(ts: number, now = Date.now()): string {
  const diff = Math.max(0, now - ts);
  const min = Math.floor(diff / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min} min ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} h ago`;
  const day = Math.floor(hr / 24);
  return `${day} day${day === 1 ? "" : "s"} ago`;
}

/**
 * One-line freshness summary for a section, shown under the status badge on the
 * list cards and detail view. Surfaces the existing `lastReportedTs` field so
 * users can gauge how stale a crowd status is.
 *
 *  - With a recent report: "Last confirmed open 3 h ago" (uses the section's
 *    `reported` consensus when present, else a generic "Last reported …").
 *  - With no recent reports: "No reports today".
 */
export function freshnessLine(s: SectionStatus, now = Date.now()): string {
  if (s.lastReportedTs == null) return "No reports today";
  const ago = relativeTime(s.lastReportedTs, now);
  if (s.reported === "open") {
    // A fresh open-detail rides along subtly, e.g. "Last confirmed open 5 min
    // ago · Long line". It's only ever set on an open section and only while
    // within OPEN_DETAIL_WINDOW_MS (the backend ages it off), so no extra
    // freshness check is needed here.
    const detail = s.openDetail ? ` · ${openDetailLabel(s.openDetail)}` : "";
    return `Last confirmed open ${ago}${detail}`;
  }
  if (s.reported === "closed") return `Last confirmed closed ${ago}`;
  return `Last reported ${ago}`;
}
